import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Gauge, AlertTriangle, Package, Gem, Image as ImageIcon, MessageSquare } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { useRates } from "@/context/RatesContext";
import { fmtINR } from "@/lib/pricing";

export default function AdminDashboard() {
  const { rates, refresh } = useRates();
  const [rateInput, setRateInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (rates?.rate_22kt) setRateInput(String(rates.rate_22kt));
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [rates?.rate_22kt]);

  const saveRate = async () => {
    const v = parseFloat(rateInput);
    if (isNaN(v) || v <= 0) {
      toast.error("Enter a valid rate");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/rates", { rate_22kt: v });
      await refresh();
      const s = await api.get("/admin/stats").then((r) => r.data);
      setStats(s);
      toast.success("Today's rates updated");
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const derived = rateInput && !isNaN(parseFloat(rateInput))
    ? {
        r22: parseFloat(rateInput),
        r24: parseFloat(rateInput) * (24 / 22),
        r18: parseFloat(rateInput) * 0.835,
        r14: parseFloat(rateInput) * 0.666,
      }
    : null;

  return (
    <div className="p-10 space-y-10">
      <div>
        <div className="ornament mb-2">Dashboard</div>
        <h1 className="font-display text-4xl text-ivory" style={{ fontWeight: 300 }}>Good day, Owner.</h1>
        <p className="text-ivory/60 mt-2 text-sm">Set today's 22kt rate — all other karats derive automatically.</p>
      </div>

      {!rates?.is_today && (
        <div className="bg-hallmark/20 border border-hallmark/50 text-rose-gold px-5 py-4 flex items-center gap-3" data-testid="dashboard-rate-warning">
          <AlertTriangle className="w-5 h-5" />
          Today's rates haven't been set yet. Customers are seeing last known prices.
        </div>
      )}

      <section className="grid lg:grid-cols-3 gap-6">
        {/* Rate input */}
        <div className="glass p-8 lg:col-span-2">
          <div className="flex items-center gap-3 mb-6">
            <Gauge className="w-5 h-5 text-rose-gold" />
            <h2 className="font-display text-2xl text-ivory">Today's Gold Rate</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 items-end">
            <div className="md:col-span-2">
              <label className="overline text-ivory/50">22kt Gold Rate (₹ per gram)</label>
              <input
                type="number"
                value={rateInput}
                onChange={(e) => setRateInput(e.target.value)}
                className="mt-2 w-full bg-ink-900 border border-white/10 px-4 py-4 text-2xl font-display text-champagne focus:border-rose-gold"
                placeholder="14300"
                data-testid="rate-22kt-input"
              />
            </div>
            <button onClick={saveRate} disabled={saving} className="bg-rose-gold text-ink-900 py-4 font-medium tracking-widest uppercase text-sm hover:bg-champagne disabled:opacity-50 flex items-center justify-center gap-2" data-testid="save-rate-button">
              <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save for today"}
            </button>
          </div>
          {derived && (
            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="derived-rates">
              <DerivedRate label="24kt" value={derived.r24} />
              <DerivedRate label="22kt" value={derived.r22} primary />
              <DerivedRate label="18kt" value={derived.r18} />
              <DerivedRate label="14kt" value={derived.r14} />
            </div>
          )}
          <div className="mt-6 pt-6 border-t border-white/5 text-xs text-ivory/40">
            Diamond rate is fixed at <span className="text-rose-gold">₹60,000 / ct</span>. Making charges:
            plain gold <span className="text-rose-gold">12%</span>, diamond <span className="text-rose-gold">15%</span>. GST <span className="text-rose-gold">3%</span> (customer-toggled).
          </div>
        </div>

        {/* Stats */}
        <div className="space-y-4">
          <StatCard icon={Package} label="Total Items" value={stats?.total_items ?? "—"} />
          <StatCard icon={Gem} label="Diamond / Plain" value={stats ? `${stats.diamond_items} / ${stats.plain_gold_items}` : "—"} />
          <StatCard icon={ImageIcon} label="Images Uploaded" value={stats ? `${stats.items_with_image} / ${stats.total_items}` : "—"} sub={stats ? `${stats.images_missing} pending` : null} />
          <StatCard icon={MessageSquare} label="Enquiries Today" value={stats?.enquiries_today ?? 0} />
        </div>
      </section>
    </div>
  );
}

const DerivedRate = ({ label, value, primary }) => (
  <div className={`p-4 border ${primary ? "border-rose-gold/50 bg-rose-gold/5" : "border-white/5 bg-ink-900/40"}`}>
    <div className="overline text-rose-gold">{label}</div>
    <div className="font-display text-xl text-ivory mt-1">{fmtINR(Math.round(value))}<span className="text-xs text-ivory/40">/g</span></div>
  </div>
);

const StatCard = ({ icon: Icon, label, value, sub }) => (
  <div className="glass p-5 flex items-center gap-4">
    <div className="w-10 h-10 bg-rose-gold/10 flex items-center justify-center border border-rose-gold/30">
      <Icon className="w-4 h-4 text-rose-gold" />
    </div>
    <div>
      <div className="overline text-ivory/50">{label}</div>
      <div className="font-display text-xl text-ivory">{value}</div>
      {sub && <div className="text-[10px] text-rose-gold/60 mt-0.5">{sub}</div>}
    </div>
  </div>
);
